import styled from "styled-components";
import { AppConfig } from "../../../AppConfig";
import { BASIC_DARK_GRAY, BASIC_LINE } from "../../../constants/colors";
import { LocalStorageManager } from "../../../core/storage/LocalStorageManager";
import { Campaign } from "../../../ducks/campaign";
import { AppliedCoupon } from "../../../ducks/coupon";
import { Button } from "../../atoms/Button";
import { CouponCodeForm } from "../../organisms/form/CouponCodeForm";
import { UseAppCouponButton } from "./UseAppCouponButton";

type Props = {
  showInput?: boolean;
  onApplyCoupon: (couponCode: string) => void;
  onRemoveCoupon: (couponUuid: string, isAppCoupon: boolean) => void;
  getCouponName: (couponCode: string) => string;
  appliedCoupons: AppliedCoupon[] | null;
  onUseAppCoupon: () => void;
};

export const CouponInput: React.FC<Props> = ({
  showInput = true,
  onApplyCoupon,
  onRemoveCoupon,
  getCouponName,
  appliedCoupons,
  onUseAppCoupon,
}) => {
  const campaign = LocalStorageManager.Instance.getObject<Campaign>(
    AppConfig.Instance.LocalStorageKey["campaign"],
  );

  if (!!campaign) {
    return null;
  }
  return (
    <CouponWrapper>
      <CouponContainer $showInput={showInput}>
        {showInput && (
          <div>
            <CouponInputTitle>クーポンコードを追加</CouponInputTitle>

            <CouponInputSubtitle>
              ※コードの大文字小文字は区別されます。
            </CouponInputSubtitle>
            <CouponCodeForm handleSubmit={onApplyCoupon} />
            {/*<UseAppCouponButton onClick={onUseAppCoupon} />*/}
          </div>
        )}

        {appliedCoupons && appliedCoupons.length > 0 && (
          <CouponInfoContainer>
            <CoupounInfoHeader>
              <CouponInfoTitle>適用済のクーポンコード</CouponInfoTitle>
              <CouponInfoWarning>
                ※アプリ連携クーポンを削除すると当該クーポンは利用できなくなります
              </CouponInfoWarning>
            </CoupounInfoHeader>

            {appliedCoupons &&
              appliedCoupons.map((coupon, i) => (
                <CouponInfoBody key={`${coupon.code}-${i}`}>
                  <CouponInfoData>
                    {coupon.isFreeCoupon ? (
                      <FreeCouponDetails>
                        <FreeCouponCodeAndName>
                          {/*<FreeCouponCode>{coupon.code}</FreeCouponCode>*/}
                          <FreeCouponName>
                            {getCouponName(coupon.code)}
                          </FreeCouponName>
                        </FreeCouponCodeAndName>
                        {coupon.menuName && (
                          <FreeCouponMenuName>
                            メニュー：{coupon.menuName}
                          </FreeCouponMenuName>
                        )}
                      </FreeCouponDetails>
                    ) : (
                      <>
                        {!coupon.isAppCoupon && (
                          <CouponInfoCode>{coupon.code}</CouponInfoCode>
                        )}
                        <CouponInfoMessage>
                          {getCouponName(coupon.code)}
                        </CouponInfoMessage>
                      </>
                    )}
                  </CouponInfoData>

                  <ButtonContainer>
                    <Button
                      size="xxs"
                      btnStyle="secondary"
                      text="削除"
                      onClick={() =>
                        onRemoveCoupon(coupon.uuid, coupon.isAppCoupon)
                      }
                    />
                  </ButtonContainer>
                </CouponInfoBody>
              ))}
          </CouponInfoContainer>
        )}
      </CouponContainer>
    </CouponWrapper>
  );
};

const CouponContainer = styled.div<{ $showInput: boolean }>`
  margin: ${({ $showInput }) => ($showInput ? "0 0 40px" : "0")};
  padding: 0;
  color: ${BASIC_DARK_GRAY};
`;

const CouponWrapper = styled.div`
  padding: 0 0 10px 0;
`;

const CouponInputTitle = styled.div`
  margin: 30px 0 18px 0;
  padding-bottom: 8px;
  text-align: left;
  font-weight: 600;
  border-bottom: 1px solid ${BASIC_LINE};
`;

const CouponInputSubtitle = styled.div`
  font-size: 12px;
  line-height: 1.1em;
  letter-spacing: 0.05em;
  padding: 0;
  color: ${BASIC_DARK_GRAY};
  text-align: left;
`;

const CouponInfoContainer = styled.div`
  margin: 25px 0 25px 0;
  text-align: left;
`;

const CoupounInfoHeader = styled.div`
  line-height: 1.25em;
  border-bottom: 1px solid rgba(112, 112, 112, 0.2);
  margin-bottom: 10px;
  padding-bottom: 10px;
`;

const CouponInfoTitle = styled.div`
  margin-bottom: 10px;
  font-weight: 600;
`;

const CouponInfoWarning = styled.div`
  font-size: 12px;
  line-height: 1.1em;
  color: ${BASIC_DARK_GRAY};
`;

const CouponInfoBody = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 8px 12px 0;
`;

const CouponInfoData = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
`;

const CouponInfoCode = styled.div`
  font-size: 12px;
  font-weight: 600;
  line-height: 1.1em;
  color: ${BASIC_DARK_GRAY};
`;

const CouponInfoMessage = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #ff0000;
`;

const ButtonContainer = styled.div`
  button {
    padding: 5px 10px;
    width: auto;
    color: ${BASIC_DARK_GRAY};
    line-height: 1.5em;
    font-size: 12px;
    letter-spacing: 0.05em;
  }
`;

const FreeCouponDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FreeCouponCodeAndName = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FreeCouponCode = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 1.1em;
  color: ${BASIC_DARK_GRAY};
`;

const FreeCouponName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #ff0000;
`;

const FreeCouponMenuName = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: ${BASIC_DARK_GRAY};
`;
